/**
 * PathBreadcrumb/index.tsx
 * desc: the header breadcrumb of the current location — Home first, the current page last, then the pin toggle
 * note: self-sufficient: the chain comes from the route table (getBreadcrumbs), nothing from the page, and a crumb's
 *       text from useCrumbLabel. A page pinned from under an entity is stored with the entity's name in front
 *       ("Order Processing / Edit") so the pin reads well out of context
 */

import { useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import { ChevronRightIcon } from '../Icon/typed';
import { getBreadcrumbs, type Crumb } from '../../router/utilities';
import { trackEvent } from '../../utilities/analytics';
import { translate } from '../../utilities/localization';
import PinButton from './PinButton';
import useCrumbLabel from './useCrumbLabel';
import './styles.css';

const CrumbLabel = ({ crumb }: { crumb: Crumb }) => <>{ useCrumbLabel(crumb) }</>;

const PathBreadcrumb = () => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    const { pathname } = useLocation();
    const crumbs = useMemo(() => getBreadcrumbs(pathname), [ pathname ]);
    // Variables
    const current: Crumb | undefined = crumbs[crumbs.length - 1];
    const parent: Crumb | undefined = crumbs[crumbs.length - 2];
    const currentLabel = useCrumbLabel(current);
    const parentLabel = useCrumbLabel(parent);


    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************
    const handleLinkClick = () => trackEvent('Global: Header - Breadcrumb Link (Click)');


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    if (!current) return null;

    const parents = crumbs.slice(0, -1);
    // an empty label means the entity is still loading — no pin is offered until every part of the label is there
    const isLabelReady = !!currentLabel && (!parent?.entity || !!parentLabel);
    const pinLabel = parent?.entity ? `${parentLabel} / ${currentLabel}` : currentLabel;

    return (
        <nav className="component-path-breadcrumb" aria-label={ translate('breadcrumb.aria.label') }>
            <ol>
                { parents.map((crumb) => (
                    <li key={ crumb.path }>
                        <Link to={ crumb.path } onClick={ handleLinkClick }><CrumbLabel crumb={ crumb }/></Link>
                        <span className="separator" aria-hidden="true"><ChevronRightIcon readOnly tooltip={ null }/></span>
                    </li>
                )) }
                <li className="current" aria-current="page">
                    <span className="label">{ currentLabel }</span>
                    <PinButton label={ isLabelReady ? pinLabel : '' } url={ current.path }/>
                </li>
            </ol>
        </nav>
    );
};

export default PathBreadcrumb;
